import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '+1-888-435-6822',
      link: 'tel:+1-888-435-6822',
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'support@cartlixs.store',
      link: 'mailto:support@cartlixs.store',
    },
    {
      icon: MapPin,
      title: 'Address',
      content: '192 A 22nd St #4699, New York, NY 30272',
      link: 'https://maps.google.com',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: 'Mon - Fri: 9:00 AM - 6:00 PM EST',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-sky-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 
            className="text-4xl md:text-5xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Contact Us
          </h1>
          <p 
            className="text-lg text-slate-600 max-w-2xl mx-auto"
            style={{ fontFamily: 'Quicksand, sans-serif' }}
          >
            Have a question or need help? We'd love to hear from you! Our team is here to assist you.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h2 
              className="text-2xl font-bold text-slate-800 mb-6"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Get in Touch
            </h2>
            
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <info.icon className="text-sky-500" size={24} />
                  </div>
                  <div>
                    <h3 
                      className="font-bold text-slate-800"
                      style={{ fontFamily: 'Nunito, sans-serif' }}
                    >
                      {info.title}
                    </h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="text-slate-600 hover:text-sky-500 transition-colors"
                        style={{ fontFamily: 'Quicksand, sans-serif' }}
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                        {info.content}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304603!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY%2C%20USA!5e0!3m2!1sen!2s!4v1634567890123!5m2!1sen!2s"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Cartlixs Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-slate-50 rounded-3xl p-6 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center">
                  <MessageCircle className="text-white" size={24} />
                </div>
                <div>
                  <h2 
                    className="text-2xl font-bold text-slate-800"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}
                  >
                    Send us a Message
                  </h2>
                  <p className="text-slate-600 text-sm">We'll get back to you within 24 hours</p>
                </div>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="text-green-500" size={32} />
                  </div>
                  <h3 
                    className="text-xl font-bold text-slate-800 mb-2"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}
                  >
                    Message Sent!
                  </h3>
                  <p className="text-slate-600 mb-6" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                    Thank you for reaching out. We'll get back to you soon.
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={() => setIsSubmitted(false)}
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="mt-1 rounded-xl"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="mt-1 rounded-xl"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="mt-1 rounded-xl"
                      required
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-1 rounded-xl min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400]"
                    data-testid="contact-submit-btn"
                  >
                    <Send className="mr-2" size={20} />
                    Send Message
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
